import React, { useRef } from 'react';
import styled from 'styled-components';
import {
    maxWidth,
    minWidth,
    width,
    margin,
    MarginProps,
    MinWidthProps,
    MaxWidthProps,
    WidthProps,
    JustifyContentProps,
} from 'styled-system';
import { useContainerDimensions } from '../utils/hooks/layout/useContainerDimensions';
import { InlineFlexBox } from '../layout/InlineFlexBox';
import loading from '../animations/loading-animation.json';
import Lottie from 'lottie-react';
import { ButtonShape, shapeVariant } from './variants/shapeVariant';
import { ButtonSize, sizeVariant } from './variants/sizeVariant';
import { ButtonDisplay, displayVariant } from './variants/displayVariant';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
    MarginProps &
    WidthProps &
    MinWidthProps &
    MaxWidthProps &
    JustifyContentProps & {
        isProcessing?: boolean;
        size?: ButtonSize;
        display?: ButtonDisplay;
        shape?: ButtonShape;
    };

/* eslint-disable */
const StyledButton = styled('button')<ButtonProps>`
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.2s;
    outline: none;
    position: relative;
    ${sizeVariant}
    ${displayVariant}
    ${shapeVariant}
    ${maxWidth}
    ${minWidth}
    ${width}
    ${margin}

    &:active, &:focus {
        outline: none;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    .spinner {
        path {
            stroke: currentColor;
        }
    }
`;
/* eslint-enable */

export const Button: React.FunctionComponent<React.PropsWithChildren<ButtonProps>> = React.forwardRef<
    HTMLButtonElement,
    ButtonProps
>(({ isProcessing, justifyContent, children, ...props }, ref) => {
    const spinnerContainerRef = useRef(null);
    const spinnerContainerDimensions = useContainerDimensions(spinnerContainerRef);

    return (
        <StyledButton {...props} ref={ref}>
            <InlineFlexBox
                ref={spinnerContainerRef}
                position="absolute"
                alignItems="center"
                justifyContent="center"
                width="100%"
                height="100%"
                left="0"
                top="0"
                className="spinner-container"
            >
                {isProcessing && (
                    <Lottie
                        loop={true}
                        animationData={loading}
                        style={{
                            width: `${spinnerContainerDimensions.width}px`,
                            height: `${spinnerContainerDimensions.height}px`,
                        }}
                    />
                )}
            </InlineFlexBox>
            <InlineFlexBox
                alignItems="center"
                justifyContent={justifyContent}
                width="100%"
                className="button-content"
                style={{ visibility: isProcessing ? 'hidden' : 'visible' }}
            >
                {children}
            </InlineFlexBox>
        </StyledButton>
    );
});

Button.displayName = 'Button';

Button.defaultProps = {
    size: 'medium',
    display: 'primary',
    shape: 'rounded',
    isProcessing: false,
    type: 'button',
    disabled: false,
    justifyContent: 'center',
};
